import {
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
} from "@angular/common/http";
import { Injectable, Injector } from "@angular/core";
import { KeycloakService } from "@inoventoryapps/keycloak-angular";
import { from, Observable } from "rxjs";
import { switchMap } from "rxjs/operators";
import { environment } from "../../environments/environment";
import { LOCALSTORAGEKEYS } from "../_constants/localStorageKeys";
import { LocalService } from "../_services/_storage/local.service";
import { KeyService } from "../_services/_utils/key.service";
import { GeographyService } from "../_services/_geography/geography.service";

@Injectable()
export class HttpHeaderConfigInterceptor implements HttpInterceptor {
  constructor(
    private localService: LocalService,
    private keyclockService: KeycloakService,
    private keyService: KeyService,
    private injector: Injector,
  ) {}

  intercept(
    req: HttpRequest<any>,
    next: HttpHandler,
  ): Observable<HttpEvent<any>> {
    if (
      req.url.includes("/assets/") ||
      req.url.split("?")[0].endsWith(".json") ||
      req.url.includes("lokos-masterdata")
    ) {
      return next.handle(req);
    }

    const profilesBase = (environment as Record<string, any>)[
      "profilesApiBaseUrl"
    ];
    if (profilesBase && req.url.startsWith(profilesBase)) {
      return from(this.buildProfilesRequest(req)).pipe(
        switchMap((profilesReq) => next.handle(profilesReq)),
      );
    }

    if (req.url.includes("api/reports/v3/reporting")) {
      return from(this.buildReportsRequest(req)).pipe(
        switchMap((reportsReq) => next.handle(reportsReq)),
      );
    }

    let headers: Record<string, any> = {
      "Cache-Control": "no-cache, no-store, max-age=0, must-revalidate",
      Pragma: "no-cache",
      "Content-Type": "application/json",
      Accept: "application/json, text/plain, */*",
      "x-encrypted": "false",
    };

    if (!req.url.includes("cdn")) {
      headers = { ...headers, "x-refresh": "true" };
    }

    const userProfile =
      this.localService.get(LOCALSTORAGEKEYS.USERPROFILE) || {};
    const userRoles = this.localService.get(LOCALSTORAGEKEYS.USERROLES) || [];
    const userName = userProfile?.name || userProfile?.preferred_username || "";
    const currentRole =
      (Array.isArray(userRoles) && userRoles.length > 0 ? userRoles[0] : "") ||
      "";
    const env = environment as Record<string, any>;

    if (req.url.includes("createOrUpdateModelclf")) {
      const geoTenant = this.injector.get(GeographyService).tenantId;
      const tenantIdentifier =
        geoTenant && geoTenant !== "dynamic_lokos"
          ? geoTenant
          : userProfile?.tenant_id ||
            userProfile?.tenantIdentifier ||
            "dynamic_lokos_cg";
      headers = {
        ...headers,
        "Ocp-Apim-Subscription-Key": env["fdmSubscriptionKey"] || "",
        user: userName,
        role: currentRole,
        "x-tenant-identifier": tenantIdentifier,
      };
    } else if (req.url.includes("fdm-integration/mclf/send")) {
      headers = {
        ...headers,
        "Ocp-Apim-Subscription-Key": env["fdmSubscriptionKey"] || "",
        user: userName,
        role: currentRole,
      };
    } else if (
      req.url.includes("v3/lokos-profile") ||
      req.url.includes("v3/lokos-profile-preprod") ||
      req.url.includes("v3/lokos-transaction") ||
      req.url.includes("v1/master/config") ||
      req.url.includes("/aajeevika/member") ||
      req.url.includes("v3/aajeevika") ||
      req.url.includes("master/config-preprod/v3")
    ) {
      headers = {
        ...headers,
        "Ocp-Apim-Subscription-Key": env["reportSubscriptionKey"] || "",
        user: userName,
        role: currentRole,
      };
    } else if (req.url.includes("profiles")) {
      headers = {
        ...headers,
        "Ocp-Apim-Subscription-Key": env["ocpApimSubscriptionKey"] || "",
      };
    } else if (req.url.includes("clf-profile")) {
      headers = {
        ...headers,
        "Ocp-Apim-Subscription-Key": env["keyClockSubscriptionKey"] || "",
        user: userName,
        role: currentRole,

        "force-update-cache": "false",
      };
    } else if (req.url.includes("api/mclf")) {
      headers = {
        ...headers,
        "Ocp-Apim-Subscription-Key":
          env["mclfSubscriptionKey"] || env["keyClockSubscriptionKey"] || "",
        "x-kcv": "secure-identity",
        user: userName,
        role: currentRole,
      };
    } else if (req.url.includes("keycloak")) {
      headers = {
        ...headers,
        "Ocp-Apim-Subscription-Key": env["lokosSubscriptionKey"] || "",
      };
    } else if (req.url.includes("v1/cadre-profile/approved")) {
      headers = {
        ...headers,
        "Ocp-Apim-Subscription-Key": env["cadreSubscriptionKey"] || "",
      };
    }

    const cloned = req.clone({ setHeaders: { ...headers } });
    return next.handle(cloned);
  }

  private async buildReportsRequest(
    req: HttpRequest<any>,
  ): Promise<HttpRequest<any>> {
    const env = environment as Record<string, any>;
    const userProfile =
      this.localService.get(LOCALSTORAGEKEYS.USERPROFILE) || {};
    const userRoles = this.localService.get(LOCALSTORAGEKEYS.USERROLES) || [];
    const userName =
      userProfile?.name || userProfile?.preferred_username || "";
    const currentRole =
      (Array.isArray(userRoles) && userRoles.length > 0 ? userRoles[0] : "") ||
      "";
    let tenantIdentifier = "dynamic_lokos";
    try {
      const geoTenant = this.injector.get(GeographyService).tenantId;
      if (geoTenant) {
        tenantIdentifier = geoTenant;
      }
    } catch {
    }
    let headers: Record<string, string> = {
      "Cache-Control": "no-cache, no-store, max-age=0, must-revalidate",
      Pragma: "no-cache",
      "Content-Type": "application/json;charset=UTF-8",
      Accept: "application/json, text/plain, */*",
      "x-encrypted": "false",
      "x-refresh": "true",
      "force-update-cache": "false",
      "x-kcv": "secure-identity",
      "Ocp-Apim-Subscription-Key": env["fdmSubscriptionKey"] || "",
      user: userName,
      role: currentRole,
      "x-tenant-identifier": tenantIdentifier,
    };
    try {
      const dpopUrl = req.url.split("?")[0].split("#")[0];
      const proof = await (this.keyclockService as any).dpopProof(
        dpopUrl,
        req.method,
        "",
        this.keyService.getKid(dpopUrl) || "",
      );
      if (proof) {
        headers = { ...headers, DPoP: proof };
      }
    } catch {
      return req.clone({ setHeaders: { ...headers } });
    }
    return req.clone({ setHeaders: { ...headers } });
  }

  private async buildProfilesRequest(
    req: HttpRequest<any>,
  ): Promise<HttpRequest<any>> {
    const env = environment as Record<string, any>;
    let headers: Record<string, string> = {
      "Cache-Control": "no-cache, no-store, max-age=0, must-revalidate",
      Pragma: "no-cache",
      Accept: "application/json, text/plain, */*",
      "Ocp-Apim-Subscription-Key": env["profileSubscriptionKey"] || "",
      "x-kcv": "secure-identity",
    };
    try {
      const dpopUrl = req.url.split('?')[0].split('#')[0];
      const proof = await (this.keyclockService as any).dpopProof(
        dpopUrl,
        req.method,
        "",
        this.keyService.getKid(dpopUrl) || "",
      );
      if (proof) {
        headers = { ...headers, DPoP: proof };
      }
    } catch {
      return req.clone({ setHeaders: { ...headers } });
    }
    return req.clone({ setHeaders: { ...headers } });
  }
}
